import { apiRequest } from './apiConfig';

// Request deduplication cache
const requestCache = new Map();

/**
 * Deduplicate API requests to prevent multiple identical calls
 * @param {string} key - Cache key for the request
 * @param {Function} requestFn - The actual request function
 * @returns {Promise} Cached or fresh request result
 */
const deduplicateRequest = async (key, requestFn) => {
  if (requestCache.has(key)) {
    return requestCache.get(key);
  }

  const promise = requestFn();
  requestCache.set(key, promise);

  // Clean up cache after request completes
  promise.finally(() => {
    setTimeout(() => requestCache.delete(key), 10000); // Cache for 10 seconds
  });

  return promise;
};

/**
 * Get user email from localStorage or return default
 * @returns {string} User email
 */
const getUserEmail = () => {
  // Return stored email or default to development user for localhost
  return localStorage.getItem('userEmail') || 'puja.romulus@srx.id';
};

/**
 * Set user email in localStorage
 * @param {string} email - User email address
 */
export const setUserEmail = email => {
  localStorage.setItem('userEmail', email);
};

/**
 * Get award roles filtered by email using Strapi v5 REST API
 * @returns {Promise<Object>} Award roles data for current user
 */
export const getAwardRoles = async () => {
  const email = getUserEmail();
  const cacheKey = `award-roles-${email}`;

  return deduplicateRequest(cacheKey, async () => {
    try {
      // Use correct Strapi v5 REST API endpoint with /api prefix
      // According to Strapi docs, all REST API endpoints must have /api prefix
      const response = await apiRequest(
        `/award-roles?filters[email][$eq]=${encodeURIComponent(email)}`
      );
      const data = await response.json();
      return data;
    } catch {
      // console.error('Error fetching award roles:', error);
      return { data: [], meta: { count: 0 } };
    }
  });
};

/**
 * Get user's accessible categories based on their roles
 * @returns {Promise<Array>} Array of category names user can access
 */
export const getUserCategories = async () => {
  try {
    const rolesData = await getAwardRoles();
    const categories =
      rolesData.data?.map(role => role.category).filter(Boolean) || [];
    return [...new Set(categories)]; // Remove duplicates
  } catch {
    // console.error('Error getting user categories:', error);
    return [];
  }
};

/**
 * Get user's accessible pillars based on their roles
 * @returns {Promise<Array>} Array of pillar names user can access
 */
export const getUserPillars = async () => {
  try {
    const rolesData = await getAwardRoles();
    const pillars =
      rolesData.data?.map(role => role.pillar).filter(Boolean) || [];
    return [...new Set(pillars)]; // Remove duplicates
  } catch {
    // console.error('Error getting user pillars:', error);
    return [];
  }
};

/**
 * Get user's roles (judge, admin, etc.)
 * @returns {Promise<Array>} Array of role names user has
 */
export const getUserRoles = async () => {
  try {
    const rolesData = await getAwardRoles();
    const roles =
      rolesData.data?.map(role => role.role_type).filter(Boolean) || [];
    return [...new Set(roles)]; // Remove duplicates
  } catch {
    // console.error('Error getting user roles:', error);
    return [];
  }
};

/**
 * Check if user has access to a specific category
 * @param {string} category - Category to check
 * @returns {Promise<boolean>} True if user has access
 */
export const hasCategoryAccess = async category => {
  try {
    const userCategories = await getUserCategories();
    return userCategories.includes(category);
  } catch {
    // console.error('Error checking category access:', error);
    return false;
  }
};

/**
 * Check if user has access to a specific pillar
 * @param {string} pillar - Pillar to check
 * @returns {Promise<boolean>} True if user has access
 */
export const hasPillarAccess = async pillar => {
  try {
    const userPillars = await getUserPillars();
    return userPillars.includes(pillar);
  } catch {
    // console.error('Error checking pillar access:', error);
    return false;
  }
};

/**
 * Check if user has a specific role
 * @param {string} role - Role to check
 * @returns {Promise<boolean>} True if user has the role
 */
export const hasRole = async role => {
  try {
    const userRoles = await getUserRoles();
    return userRoles.includes(role);
  } catch {
    // console.error('Error checking role access:', error);
    return false;
  }
};

export const getAwardSubmissions = async (
  year: number
): Promise<{
  data: any[];
  meta: any;
}> => {
  const startDate = `${year}-01-01T00:00:00.000Z`;
  const endDate = `${year}-12-31T23:59:59.999Z`;
  const yearFilter =
    `&filters[createdAt][$gte]=${encodeURIComponent(startDate)}` +
    `&filters[createdAt][$lte]=${encodeURIComponent(endDate)}`;

  let allData = [];
  let page = 1;
  let hasMorePages = true;
  const pageSize = 100; // Maximum recommended page size

  while (hasMorePages) {
    try {
      const response = await apiRequest(
        `/award-submissions?pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true${yearFilter}`,
        {
          method: 'GET',
        }
      );

      const result = await response.json();

      if (!result.data || !Array.isArray(result.data)) {
        console.error(
          `❌ Invalid award-submissions data received from API (page ${page})`
        );
        throw new Error(
          `Invalid award-submissions data received from API (page ${page})`
        );
      }

      // Add current page data to total
      allData = allData.concat(result.data);

      // Check pagination metadata for more pages
      const pagination = result.meta?.pagination;
      if (pagination) {
        hasMorePages = page < pagination.pageCount;

        // Log progress for large datasets (commented out)
        // if (pagination.pageCount > 1) {
        //   console.log(`📄 Fetched award-submissions page ${page}/${pagination.pageCount} (${result.data.length} items, ${allData.length}/${pagination.total} total)`);
        // }
      } else {
        hasMorePages = false;
      }

      page++;
    } catch (error) {
      console.error(`❌ Error fetching award submissions page ${page}:`, error);
      // Return what we have so far instead of failing completely
      break;
    }
  }

  // console.log(`✅ Fetched ${allData.length} award-submissions total`);
  return { data: allData, meta: { count: allData.length } };
};

/**
 * Get award submissions by category with email authentication
 * @param {string} category - Category to filter by
 * @returns {Promise<Object>} Award submissions filtered by category
 */
export const getAwardSubmissionsByCategory = async category => {
  const email = getUserEmail();

  try {
    const response = await apiRequest(
      `/award-submissions?filters[category][$eq]=${encodeURIComponent(category)}`,
      {
        headers: {
          'X-Auth-Info': email,
        },
      }
    );
    const data = await response.json();
    return data;
  } catch {
    // console.error('Error fetching award submissions by category:', error);
    return { data: [], meta: { count: 0 } };
  }
};

/**
 * Get award submissions by pillar with email authentication
 * @param {string} pillar - Pillar to filter by
 * @returns {Promise<Object>} Award submissions filtered by pillar
 */
export const getAwardSubmissionsByPillar = async pillar => {
  const email = getUserEmail();

  try {
    const response = await apiRequest(
      `/award-submissions?filters[pillar][$eq]=${encodeURIComponent(pillar)}`,
      {
        headers: {
          'X-Auth-Info': email,
        },
      }
    );
    const data = await response.json();
    return data;
  } catch {
    // console.error('Error fetching award submissions by pillar:', error);
    return { data: [], meta: { count: 0 } };
  }
};

/**
 * Get award submissions by status with email authentication
 * @param {string} status - Status to filter by
 * @returns {Promise<Object>} Award submissions filtered by status
 */
export const getAwardSubmissionsByStatus = async status => {
  const email = getUserEmail();

  try {
    const response = await apiRequest(
      `/award-submissions?filters[status][$eq]=${encodeURIComponent(status)}`,
      {
        headers: {
          'X-Auth-Info': email,
        },
      }
    );
    const data = await response.json();
    return data;
  } catch {
    // console.error('Error fetching award submissions by status:', error);
    return { data: [], meta: { count: 0 } };
  }
};

/**
 * Get current user email
 * @returns {string} Current user email
 */
export const getCurrentUserEmail = () => {
  return getUserEmail();
};
